import { ReactNode, useEffect, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import {
  CustomerFacetState,
  CustomerHierarchyTree as HierarchyTree,
} from './customerFacets';

export interface CustomerHierarchyTreeProps {
  tree: HierarchyTree;
  facets: CustomerFacetState;
  onSelectAll: () => void;
  onSelectGroup: (groupName: string) => void;
  onSelectSubNode: (groupName: string, subNodeName: string) => void;
  onSelectIndependents: () => void;
  onSelectUnassigned: () => void;
}

function HierarchyRow({
  name,
  count,
  isActive,
  indent,
  chevron,
  badge,
  onSelect,
}: {
  name: string;
  count: number;
  isActive: boolean;
  indent: number;
  chevron: ReactNode;
  badge?: ReactNode;
  onSelect: () => void;
}) {
  return (
    <div
      className={`flex items-center rounded-md ${
        isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
      }`}
    >
      <span className='flex h-6 w-6 shrink-0 items-center justify-center'>
        {chevron}
      </span>
      <button
        type='button'
        onClick={onSelect}
        style={{ paddingLeft: `${indent}rem` }}
        className={`flex flex-1 items-center gap-1.5 py-1.5 pr-2.5 text-left text-sm ${
          isActive ? 'font-medium text-primary' : 'text-gray-700'
        }`}
      >
        <span className='truncate'>{name}</span>
        {badge}
        <span className='ml-auto shrink-0 text-xs'>{count}</span>
      </button>
    </div>
  );
}

function NodeBadge() {
  return (
    <span className='shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500'>
      node
    </span>
  );
}

export function CustomerHierarchyTree({
  tree,
  facets,
  onSelectAll,
  onSelectGroup,
  onSelectSubNode,
  onSelectIndependents,
  onSelectUnassigned,
}: CustomerHierarchyTreeProps) {
  const activeGroup =
    facets.hierarchy.kind === 'group' || facets.hierarchy.kind === 'subNode'
      ? facets.hierarchy.groupName
      : null;

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );

  const expand = (groupName: string) =>
    setCollapsedGroups((previous) => {
      if (!previous.has(groupName)) return previous;
      const next = new Set(previous);
      next.delete(groupName);
      return next;
    });

  useEffect(() => {
    if (activeGroup) expand(activeGroup);
  }, [activeGroup]);

  const toggleExpanded = (groupName: string) =>
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });

  const handleSelectGroup = (groupName: string) => {
    onSelectGroup(groupName);
    expand(groupName);
  };

  const chevronButton = (groupName: string, isExpanded: boolean) => (
    <button
      type='button'
      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${groupName}`}
      onClick={() => toggleExpanded(groupName)}
      className='rounded p-0.5 text-gray-400 hover:bg-gray-100'
    >
      <ChevronRightIcon
        className={`h-4 w-4 transition-transform ${
          isExpanded ? 'rotate-90' : ''
        }`}
      />
    </button>
  );

  return (
    <div className='flex flex-col gap-0.5'>
      <HierarchyRow
        name='All customers'
        count={tree.total}
        isActive={facets.hierarchy.kind === 'all'}
        indent={0}
        chevron={null}
        onSelect={onSelectAll}
      />

      {tree.groups.map((group) => {
        const isExpanded = !collapsedGroups.has(group.name);
        return (
          <div key={group.name} className='flex flex-col gap-0.5'>
            <HierarchyRow
              name={group.name}
              count={group.count}
              isActive={
                facets.hierarchy.kind === 'group' &&
                facets.hierarchy.groupName === group.name
              }
              indent={0}
              chevron={
                group.subNodes.length
                  ? chevronButton(group.name, isExpanded)
                  : null
              }
              badge={<NodeBadge />}
              onSelect={() => handleSelectGroup(group.name)}
            />
            {isExpanded &&
              group.subNodes.map((subNode) => (
                <HierarchyRow
                  key={subNode.name}
                  name={subNode.name}
                  count={subNode.count}
                  isActive={
                    facets.hierarchy.kind === 'subNode' &&
                    facets.hierarchy.groupName === group.name &&
                    facets.hierarchy.subNodeName === subNode.name
                  }
                  indent={1.25}
                  chevron={null}
                  onSelect={() => onSelectSubNode(group.name, subNode.name)}
                />
              ))}
          </div>
        );
      })}

      {tree.independents > 0 && (
        <HierarchyRow
          name='Independents'
          count={tree.independents}
          isActive={facets.hierarchy.kind === 'independents'}
          indent={0}
          chevron={null}
          onSelect={onSelectIndependents}
        />
      )}

      {tree.unassigned > 0 && (
        <HierarchyRow
          name='Unassigned'
          count={tree.unassigned}
          isActive={facets.hierarchy.kind === 'unassigned'}
          indent={0}
          chevron={null}
          onSelect={onSelectUnassigned}
        />
      )}
    </div>
  );
}
